import { supabase } from '../../lib/supabaseClient.js';
import { useState } from 'react';
import Cropper from 'react-easy-crop';
import './ClubCreateForm.css';

// 동아리 생성 폼 컴포넌트
export default function ClubCreateForm({ onClose }) {

    // 동아리 정보 저장
    const [club, setClub] = useState({
        name: '',
        member_count: '',
        description: '',
        fee: '',
        eligibility: '',
        activities: '',
        category: [],
    });

    // 최종 업로드할 이미지 파일
    const [image, setImage] = useState(null);

    // 이미지 미리보기 URL
    const [previewImage, setPreviewImage] = useState(null);

    // 이미지 자르기 관련 상태
    const [showCropModal, setShowCropModal] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    // 입력 필드 정보
    const fields = [
        { label: '동아리명', name: 'name' },
        { label: '인원', name: 'member_count', placeholder: '본인 미포함 (혼자면 0)' },
        { label: '간단 설명', name: 'description' },
        { label: '회비', name: 'fee', placeholder: '예) 25,000~30,000' },
        { label: '지원 자격', name: 'eligibility', placeholder: '조건1/조건2/조건3' },
        { label: '주요 활동', name: 'activities', placeholder: '활동1/활동2/활동3' },
    ];

    // 카테고리 목록
    const categories = ['음악', '댄스', '스포츠', '과학', '코딩', '봉사', '사회', '게임', '종교', '스터디', '학술'];

    // 소속 목록
    const clubTypes = ['중앙', '비중앙'];

    // 입력값 변경
    function handleChange(e) {
        const { name, value } = e.target;
        setClub({ ...club, [name]: value });
    }

    // 이미지 선택
    function handleImageChange(e) {
        const file = e.target.files[0];
        if (!file) return;

        const imageUrl = URL.createObjectURL(file);

        setPreviewImage(imageUrl);
        setShowCropModal(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
    }

    // 이미지 자르기 완료 시 영역 저장
    function onCropComplete(_, croppedPixels) {
        setCroppedAreaPixels(croppedPixels);
    }

    // 잘린 이미지를 파일로 생성
    async function getCroppedImage(imageSrc, cropPixels) {
        const imageElement = new Image();
        imageElement.src = imageSrc;

        await new Promise((resolve) => {
            imageElement.onload = resolve;
        });

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = cropPixels.width;
        canvas.height = cropPixels.height;

        ctx.drawImage(
            imageElement,
            cropPixels.x,
            cropPixels.y,
            cropPixels.width,
            cropPixels.height,
            0,
            0,
            cropPixels.width,
            cropPixels.height
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                const croppedFile = new File(
                    [blob],
                    `club_banner_${Date.now()}.jpg`,
                    { type: 'image/jpeg' }
                );

                resolve(croppedFile);
            }, 'image/jpeg');
        });
    }

    // 자른 이미지 저장
    async function handleCropSave() {
        const croppedFile = await getCroppedImage(previewImage, croppedAreaPixels);

        setImage(croppedFile);
        setShowCropModal(false);
    }

    // 카테고리 및 소속 선택
    function handleCategory(categoryName) {
        const clubTypes = ['중앙', '비중앙'];

        // 소속은 하나만 선택 가능
        if (clubTypes.includes(categoryName)) {
            const filteredCategory = club.category.filter(
                (item) => !clubTypes.includes(item)
            );

            if (club.category.includes(categoryName)) {
                setClub({ ...club, category: filteredCategory });
            } else {
                setClub({ ...club, category: [...filteredCategory, categoryName] });
            }
            return;
        }

        // 일반 카테고리는 다중 선택 가능
        if (club.category.includes(categoryName)) {
            setClub({
                ...club,
                category: club.category.filter((item) => item !== categoryName),
            });
        } else {
            setClub({ ...club, category: [...club.category, categoryName] });
        }
    }

    // 동아리 생성 요청
    async function handleSubmit() {

        // 로그인 사용자 정보 조회
        const { data: authData } = await supabase.auth.getUser();
        const authUser = authData?.user;

        if (!authUser) {
            alert('로그인을 해주세요!');
            return;
        }

        // users 테이블에서 사용자 id 조회
        const { data: userData, error: userError } = await supabase
            .from('users')
            .select('id')
            .eq('email', authUser.email)
            .maybeSingle();

        if (userError || !userData) {
            console.log(userError);
            alert('유저 정보를 찾을 수 없습니다.');
            return;
        }

        let imageUrl = '';

        // 이미지 업로드
        if (image) {
            const fileName = `${Date.now()}_${image.name}`;

            const { error: uploadError } = await supabase.storage
                .from('club-images')
                .upload(fileName, image);

            if (uploadError) {
                console.log(uploadError);
                alert('이미지 업로드 실패');
                return;
            }

            // 업로드한 이미지 URL 생성
            const { data: urlData } = supabase.storage
                .from('club-images')
                .getPublicUrl(fileName);

            imageUrl = urlData.publicUrl;
        }

        // clubs 테이블에 동아리 정보 저장
        const { error } = await supabase
            .from('clubs')
            .insert({
                name: club.name,
                member_count: club.member_count,
                description: club.description,
                fee: club.fee,
                eligibility: club.eligibility,
                activities: club.activities,
                image_url: imageUrl,
                category: club.category,
                created_by: userData.id,
                approval_status: '대기',
            });

        if (error) {
            console.log(error);
            alert(error.message);
            return;
        }

        alert('동아리 신청이 완료되었습니다. 관리자 승인 후 개설됩니다.');

        // 입력값 초기화
        setClub({
            name: '',
            member_count: '',
            description: '',
            fee: '',
            eligibility: '',
            activities: '',
            category: [],
        });

        setImage(null);
        setPreviewImage(null);

        // 모달 닫기
        onClose();
    }

    return (
        <div className="clubCreate">

            {/* 닫기 버튼 */}
            <button className="clubCreate-close" onClick={onClose}>X</button>

            {/* 입력 필드 */}
            {fields.map((field) => (
                <div key={field.name} className="clubCreate-row">
                    <label
                        className={
                            field.name === 'name'
                                ? 'clubCreate-label clubCreate-title'
                                : 'clubCreate-label'
                        }
                    >
                        {field.label}:
                    </label>

                    <input
                        className="clubCreate-input"
                        type="text"
                        name={field.name}
                        value={club[field.name]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                    />
                </div>
            ))}

            {/* 카테고리 선택 */}
            <div className="clubCreate-row">
                <label className="clubCreate-label">카테고리:</label>
                <div className="clubCreate-category-wrap">
                    {categories.map((category) => (
                        <button
                            key={category}
                            type="button"
                            className={
                                club.category.includes(category)
                                    ? 'clubCreate-category selected'
                                    : 'clubCreate-category'
                            }
                            onClick={() => handleCategory(category)}
                        >
                            {category}
                        </button>
                    ))}
                </div>
            </div>

            {/* 소속 선택 */}
            <div className="clubCreate-row">
                <label className="clubCreate-label">소속:</label>
                <div className="clubCreate-category-wrap">
                    {clubTypes.map((type) => (
                        <button
                            key={type}
                            type="button"
                            className={
                                club.category.includes(type)
                                    ? 'clubCreate-category selected'
                                    : 'clubCreate-category'
                            }
                            onClick={() => handleCategory(type)}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>

            {/* 이미지 업로드 */}
            <div className="clubCreate-row">
                <label className="clubCreate-label">동아리 이미지 등록:</label>
                <label className={`image-upload-button ${image ? 'uploaded' : ''}`}>
                    {image ? image.name : '이미지 추가'}
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        hidden
                    />
                </label>
            </div>

            {/* 이미지 안내 문구 */}
            <p className="image-guide">
                ※ 프로필 이미지는 1:1 비율(500 × 500 이상)을 권장합니다.
            </p>

            {/* 제출 버튼 */}
            <button
                className="clubCreate-submit"
                type="button"
                onClick={handleSubmit}
            >
                추가하기
            </button>

            {/* 이미지 자르기 모달 */}
            {showCropModal && (
                <div className="cropModal-bg">
                    <div className="cropModal">
                        <h2>배너 이미지 조정</h2>

                        <div className="crop-area">
                            <Cropper
                                image={previewImage}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        </div>

                        {/* 확대 비율 조절 */}
                        <input
                            className="crop-zoom"
                            type="range"
                            min={1}
                            max={3}
                            step={0.1}
                            value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                        />

                        <div className="crop-buttons">
                            <button
                                type="button"
                                onClick={() => setShowCropModal(false)}
                            >
                                취소
                            </button>

                            <button
                                type="button"
                                onClick={handleCropSave}
                            >
                                완료
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}